// ABOUTME: Custom hook that derives all variation code display flags and descriptions for an order.
// Centralizes the repeated isRMC + hasVariationCode pattern used across multiple order components.

import {timeFormatter} from '@hconnect/uikit'
import {useTranslation} from 'react-i18next'

import type {Order} from '../Order.types'
import {getIsRMC} from '../Order.utils'
import {
  hasVariationCode,
  isDateDangerCode,
  isDateStrikethroughCode,
  isDestinationCode,
  isInlineBannerCode,
  isTopBannerCode,
  getVariationCodeDescription,
  formatTimespanOffset,
  isDateStrikethroughWhenOffsetNotZero
} from '../variationCode.utils'

interface VariationCodeResult {
  showVariation: boolean
  variationCode: string
  showDateAsterisk: boolean
  showDateDanger: boolean
  showDateStrikethrough: boolean
  showDateStrikethroughOffsetNotZero: boolean
  showAdjustedTimeAsPrimary: boolean
  showInlineBanner: boolean
  showTopBanner: boolean
  showDestinationAsterisk: boolean
  showEstimatedArrival: boolean
  adjustedVariationTime: string
  showRequestedDateTimeWithStrikeThrough: boolean
  variationDescription: string | null
  destinationDescription: string | null
}

export const useVariationCode = (order: Order, eventLocationOffset?: number): VariationCodeResult => {
  const {
    t,
    i18n: {language}
  } = useTranslation()

  const isRMC = !!getIsRMC(order)
  const showVariation = isRMC && hasVariationCode(order)
  const variationCode = order.variationCode || ''
  const variationCodeOffset = order.variationCodeOffset || ''

  const showDateAsterisk = showVariation && !isDestinationCode(variationCode)
  const showDateDanger = showVariation && isDateDangerCode(variationCode) && formatTimespanOffset(variationCodeOffset, t) === 'PT0S'
  const showDateStrikethrough = showVariation && isDateStrikethroughCode(variationCode)
  const showDateStrikethroughOffsetNotZero = showVariation && isDateStrikethroughWhenOffsetNotZero(variationCode) && formatTimespanOffset(variationCodeOffset, t) !== 'PT0S'
  const showInlineBanner = showVariation && isInlineBannerCode(variationCode)
  const showTopBanner = showVariation && isTopBannerCode(variationCode)
  const showDestinationAsterisk = showVariation && isDestinationCode(variationCode)
  const showEstimatedArrival =
    showDateStrikethrough && !!order.variationCodeAdjustedTime
  // SE/SL no longer receive variationCodeAdjustedTime from BE — primary date is always scheduleStartDate
  const showAdjustedTimeAsPrimary = false

  const variationDescription =
    showVariation && !isDestinationCode(variationCode) && !showDateStrikethrough
      ? getVariationCodeDescription(order, t, language, eventLocationOffset)
      : null

  const destinationDescription = showDestinationAsterisk
    ? getVariationCodeDescription(order, t, language, eventLocationOffset)
    : null

  const adjustedVariationTime = order.variationCodeAdjustedTime
    ? timeFormatter(order.variationCodeAdjustedTime, language, eventLocationOffset)
    : ''

  const shouldShowAdjustedBeforeStruckTime =
    showDateStrikethroughOffsetNotZero && !!adjustedVariationTime

  return {
    showVariation,
    variationCode,
    showDateAsterisk,
    showDateDanger,
    showDateStrikethrough,
    showDateStrikethroughOffsetNotZero,
    showAdjustedTimeAsPrimary,
    showInlineBanner,
    showTopBanner,
    showDestinationAsterisk,
    showEstimatedArrival,
    adjustedVariationTime,
    showRequestedDateTimeWithStrikeThrough: shouldShowAdjustedBeforeStruckTime,
    variationDescription,
    destinationDescription
  }
}
